import * as React from 'react'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { MessageCircleDashed, Check, Send, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { AskUserQuestionRequest, AskUserQuestionAnswer } from '@/shared/types'
import type { AskUserQuestionStructuredResponse } from './types'

interface AskUserQuestionInputProps {
  request: AskUserQuestionRequest
  onResponse: (response: AskUserQuestionStructuredResponse) => void
  unstyled?: boolean
}

export function AskUserQuestionInput({ request, onResponse, unstyled = false }: AskUserQuestionInputProps) {
  const { questions, requestId } = request
  const [activeTab, setActiveTab] = useState(0)

  // Per-question selections: questionIndex -> Set of selected labels
  const [selections, setSelections] = useState<Record<number, Set<string>>>({})

  // Per-question "Other" text
  const [otherTexts, setOtherTexts] = useState<Record<number, string>>({})

  // Per-question "Other" selected state
  const [otherSelected, setOtherSelected] = useState<Record<number, boolean>>({})

  // Reset local state when a new request arrives
  useEffect(() => {
    setActiveTab(0)
    setSelections({})
    setOtherTexts({})
    setOtherSelected({})
  }, [requestId])

  const currentQuestion = questions[activeTab]
  const isMultiSelect = currentQuestion?.multiSelect ?? false

  const toggleOption = useCallback((questionIndex: number, label: string, multiSelect: boolean) => {
    setSelections(prev => {
      if (multiSelect) {
        const current = new Set(prev[questionIndex] ?? [])
        if (current.has(label)) current.delete(label)
        else current.add(label)
        return { ...prev, [questionIndex]: current }
      } else {
        // Single-select: replace selection and deselect "Other"
        setOtherSelected(os => ({ ...os, [questionIndex]: false }))
        return { ...prev, [questionIndex]: new Set([label]) }
      }
    })
  }, [])

  const toggleOther = useCallback((questionIndex: number, multiSelect: boolean) => {
    setOtherSelected(prev => {
      const wasSelected = prev[questionIndex] ?? false
      if (!multiSelect && !wasSelected) {
        // Single-select: deselect all predefined options
        setSelections(s => ({ ...s, [questionIndex]: new Set() }))
      }
      return { ...prev, [questionIndex]: !wasSelected }
    })
  }, [])

  const handleOtherTextChange = useCallback((questionIndex: number, text: string) => {
    setOtherTexts(prev => ({ ...prev, [questionIndex]: text }))
  }, [])

  // Check if every question has at least one selection
  const canSubmit = useMemo(() => {
    return questions.every((_, i) => {
      const hasSelection = (selections[i]?.size ?? 0) > 0
      const hasOther = (otherSelected[i] ?? false) && (otherTexts[i]?.trim().length ?? 0) > 0
      return hasSelection || hasOther
    })
  }, [questions, selections, otherSelected, otherTexts])

  // Check which tabs are answered
  const isTabAnswered = useCallback((index: number) => {
    const hasSelection = (selections[index]?.size ?? 0) > 0
    const hasOther = (otherSelected[index] ?? false) && (otherTexts[index]?.trim().length ?? 0) > 0
    return hasSelection || hasOther
  }, [selections, otherSelected, otherTexts])

  const handleSubmit = useCallback(() => {
    const answers: AskUserQuestionAnswer[] = questions.map((_, i) => {
      const selected = Array.from(selections[i] ?? [])
      const otherText = (otherSelected[i] && otherTexts[i]?.trim()) || undefined
      return { selected, ...(otherText ? { otherText } : {}) }
    })
    onResponse({ type: 'ask_user_question', requestId, answers })
  }, [questions, selections, otherSelected, otherTexts, requestId, onResponse])

  const isLastTab = activeTab === questions.length - 1

  // Keyboard handling
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return

    const target = e.target as HTMLElement
    const isInInput = target.tagName === 'INPUT'
    const isButtonLike = !isInInput && !!target.closest('button, [role="button"]')
    if (isButtonLike) return // Let footer buttons handle their own Enter

    if (questions.length > 1 && !isLastTab) {
      // Navigate to next question — works even from the Other text input
      e.preventDefault()
      setActiveTab(t => t + 1)
    } else if (canSubmit && !isInInput) {
      // Submit on last question — but not while typing in the Other input
      e.preventDefault()
      handleSubmit()
    }
  }, [questions.length, isLastTab, canSubmit, handleSubmit])

  if (!currentQuestion) return null

  return (
    <div
      className={cn(
        'overflow-hidden h-full flex flex-col',
        unstyled ? 'border-0' : 'border border-accent/20 rounded-[8px] shadow-middle'
      )}
      onKeyDown={handleKeyDown}
    >
      <div className="p-4 space-y-3 flex-1 min-h-0 flex flex-col">
        {/* Header with icon */}
        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <MessageCircleDashed className="h-3.5 w-3.5 text-accent" />
          <span>Clarifying Questions</span>
        </div>

        {/* Question tabs (only if multiple questions) */}
        {questions.length > 1 && (
          <div className="flex gap-1.5 flex-wrap">
            {questions.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveTab(i)}
                className={cn(
                  'px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors',
                  i === activeTab
                    ? 'bg-accent/15 text-accent border-accent/30'
                    : isTabAnswered(i)
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-muted text-muted-foreground border-border/50 hover:border-border'
                )}
              >
                {q.header}{isTabAnswered(i) && i !== activeTab ? ' ✓' : ''}
              </button>
            ))}
          </div>
        )}

        {/* Question text */}
        <div className="text-xs leading-[18px] text-foreground">
          {currentQuestion.question}
        </div>

        {/* Options */}
        <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto">
          {currentQuestion.options.map((option, oIndex) => {
            const isSelected = selections[activeTab]?.has(option.label) ?? false
            return (
              <button
                key={oIndex}
                type="button"
                onClick={() => toggleOption(activeTab, option.label, isMultiSelect)}
                className={cn(
                  'w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-colors',
                  isSelected
                    ? 'border-accent/40 bg-accent/5'
                    : 'border-border/50 bg-muted/30 hover:border-border hover:bg-muted/50'
                )}
              >
                {/* Radio or Checkbox indicator */}
                <div className={cn(
                  'mt-0.5 shrink-0 w-4 h-4 border flex items-center justify-center transition-colors',
                  isMultiSelect ? 'rounded-sm' : 'rounded-full',
                  isSelected
                    ? 'border-accent bg-accent text-accent-foreground'
                    : 'border-muted-foreground/30'
                )}>
                  {isSelected && (isMultiSelect
                    ? <Check className="w-3 h-3" />
                    : <div className="w-2 h-2 rounded-full bg-current" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-foreground">{option.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{option.description}</div>
                </div>
              </button>
            )
          })}

          {/* "Other" as a selectable option — uses div instead of button
              to prevent Space key from toggling selection while typing */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => toggleOther(activeTab, isMultiSelect)}
            className={cn(
              'w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-colors cursor-pointer',
              (otherSelected[activeTab] ?? false)
                ? 'border-accent/40 bg-accent/5'
                : 'border-border/50 bg-muted/30 hover:border-border hover:bg-muted/50'
            )}
          >
            <div className={cn(
              'mt-0.5 shrink-0 w-4 h-4 border flex items-center justify-center transition-colors',
              isMultiSelect ? 'rounded-sm' : 'rounded-full',
              (otherSelected[activeTab] ?? false)
                ? 'border-accent bg-accent text-accent-foreground'
                : 'border-muted-foreground/30'
            )}>
              {(otherSelected[activeTab] ?? false) && (isMultiSelect
                ? <Check className="w-3 h-3" />
                : <div className="w-2 h-2 rounded-full bg-current" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-foreground">Other</div>
              {(otherSelected[activeTab] ?? false) && (
                <input
                  type="text"
                  autoFocus
                  placeholder="Type your own answer..."
                  value={otherTexts[activeTab] ?? ''}
                  onChange={(e) => handleOtherTextChange(activeTab, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1.5 w-full bg-background/50 border border-border/50 rounded-md px-2.5 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-accent/40"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer row */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border/50">
        {questions.length > 1 && !isLastTab ? (
          <>
            {activeTab > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1"
                onClick={() => setActiveTab(t => t - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              variant="default"
              className="h-7 gap-1"
              onClick={() => setActiveTab(t => t + 1)}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <div className="flex-1" />
            <span className="text-[10px] text-muted-foreground">
              {activeTab + 1}/{questions.length}
            </span>
          </>
        ) : (
          <>
            {questions.length > 1 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1"
                onClick={() => setActiveTab(t => t - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              variant="default"
              className="h-7 gap-1.5"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              <Send className="h-3.5 w-3.5" />
              Submit
            </Button>
            <div className="flex-1" />
            <span className="text-[10px] text-muted-foreground">
              <kbd className="px-1 py-0.5 bg-muted border border-border/50 rounded text-[9px]">Enter</kbd>
              {' '}submit
            </span>
          </>
        )}
      </div>
    </div>
  )
}
